import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../context/AuthContext";

import { getProjectReport } from "../services/reportService";
import { getMyProjects } from "../services/projectService";

import KpiCard from "../components/KpiCard";

/* ========================================================= */
/* Dashboard                                                 */
/* ========================================================= */

export default function Dashboard() {
  const { companyId } = useAuth();

  /* ---------------- STATE ---------------- */

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");

  const [report, setReport] = useState(null);

  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  const [error, setError] = useState("");

  /* ---------------- DERIVED ---------------- */

  const selectedProject = useMemo(
    () => projects.find((p) => p._id === projectId),
    [projects, projectId]
  );

  /* ======================================================= */
  /* Load Projects                                           */
  /* ======================================================= */

  useEffect(() => {
    if (!companyId) return;

    let active = true;

    const loadProjects = async () => {
      setLoadingProjects(true);
      setError("");

      try {
        const data = await getMyProjects();
        const list = Array.isArray(data) ? data : [];

        if (!active) return;

        setProjects(list);

        if (list.length === 0) {
          setProjectId("");
          return;
        }

        const last = localStorage.getItem("lastProjectId");

        const initial =
          (last && list.find((p) => p._id === last)?._id) ||
          list[0]._id;

        setProjectId(initial);
        localStorage.setItem("lastProjectId", initial);
      } catch (err) {
        if (active) setError(err.message || "Failed to load projects");
      } finally {
        if (active) setLoadingProjects(false);
      }
    };

    loadProjects();

    return () => {
      active = false;
    };
  }, [companyId]);

  /* ======================================================= */
  /* Load Report (SOURCE OF TRUTH)                           */
  /* ======================================================= */

  useEffect(() => {
    if (!projectId) {
      setReport(null);
      return;
    }

    let active = true;

    const loadReport = async () => {
      setLoadingReport(true);

      try {
        const data = await getProjectReport(projectId);
        if (active) setReport(data);
      } catch (err) {
        console.error("Report load failed:", err.message);
        if (active) setReport(null);
      } finally {
        if (active) setLoadingReport(false);
      }
    };

    loadReport();

    return () => {
      active = false;
    };
  }, [projectId]);

  /* ======================================================= */
  /* KPI Stats (FROM REPORT ONLY)                            */
  /* ======================================================= */

  const taskStats = useMemo(() => {
    return {
      total: report?.tasks?.total || 0,
      completed: report?.tasks?.completed || 0,
      pending: report?.tasks?.pending || 0,
      overdue: report?.tasks?.overdue || 0,
    };
  }, [report]);

  const completionRate =
  report?.tasks?.completionRate || 0;

const phaseBreakdown =
  report?.tasks?.byPhase || [];

const upcomingMilestones = Array.isArray(report?.milestones?.upcoming)

  ? report.milestones.upcoming

  : [];

const deadlineStatus =
  report?.project?.deadlineStatus || "not_set";

  /* ======================================================= */
  /* Upcoming Milestones (FROM REPORT)                       */
  /* ======================================================= */



  /* ======================================================= */
  /* Guards                                                  */
  /* ======================================================= */

  if (!companyId) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Select a company to continue.
      </div>
    );
  }

  if (!loadingProjects && projects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white border rounded-xl p-6 text-center">
          <h2 className="text-lg font-semibold mb-2">
            No projects yet
          </h2>

          <Link
            to="/projects"
            className="inline-block bg-gray-900 text-white px-4 py-2 rounded-lg text-sm"
          >
            Create Project
          </Link>
        </div>
      </div>
    );
  }

  /* ======================================================= */
  /* UI                                                      */
  /* ======================================================= */
return (
    <div className="mx-auto max-w-7xl p-6">
{/* HEADER */}
<div className="mb-6">
  <h1 className="text-2xl font-semibold text-gray-900">
    Dashboard
  </h1>

  <p className="mt-1 text-sm text-gray-500">
    {selectedProject
      ? `Tracking: ${selectedProject.name}`
      : "Track your projects"}
  </p>
</div>

{/* ERROR */}
{error && (
  <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
    {error}
  </div>
)}

{/* PROJECT SELECTOR */}
<div className="mb-6 rounded-xl border bg-white p-4">
  {loadingProjects ? (
    <span className="text-sm text-gray-500">
      Loading projects...
    </span>
  ) : (
    <select
      value={projectId}
      onChange={(e) => {
        const id = e.target.value;
        setProjectId(id);
        localStorage.setItem(
          "lastProjectId",
          id
        );
      }}
      className="
        w-full
        max-w-md
        rounded-lg
        border
        px-3
        py-2
        text-sm
      "
    >
      {projects.map((p) => (
        <option
          key={p._id}
          value={p._id}
        >
          {p.name || "Untitled"}
        </option>
      ))}
    </select>
  )}
</div>

{/* KPI CARDS */}
<div className="grid grid-cols-2 gap-4 lg:grid-cols-5 mb-6">
  <KpiCard
    title="Total Tasks"
    value={taskStats.total}
    loading={loadingReport}
  />

  <KpiCard
    title="Completed"
    value={taskStats.completed}
    loading={loadingReport}
  />

  <KpiCard
    title="Pending"
    value={taskStats.pending}
    loading={loadingReport}
  />

  <KpiCard
    title="Overdue"
    value={taskStats.overdue}
    loading={loadingReport}
  />

  <KpiCard
    title="Completion %"
    value={`${completionRate}%`}
    loading={loadingReport}
  />
</div>

{/* MAIN DASHBOARD */}
<div className="grid gap-6 lg:grid-cols-2">

  {/* PHASE PROGRESS */}
  <div className="rounded-xl border bg-white p-5">
    <h2 className="mb-4 text-lg font-semibold">
      Phase Progress
    </h2>

    {loadingReport ? (
      <div className="text-sm text-gray-500">
        Loading...
      </div>
    ) : phaseBreakdown.length === 0 ? (
      <div className="text-sm text-gray-500">
        No phases found
      </div>
    ) : (
      <div className="space-y-3">
        {phaseBreakdown.map((phase) => (
          <div
            key={phase._id}
            className="flex items-center justify-between"
          >
            <span className="truncate">
              {phase.name}
            </span>

            <span className="font-medium">
              {phase.progress || 0}%
            </span>
          </div>
        ))}
      </div>
    )}
  </div>

  {/* DEADLINE HEALTH */}
  <div className="rounded-xl border bg-white p-5">
    <h2 className="mb-4 text-lg font-semibold">
      Deadline Health
    </h2>

    <div className="space-y-3">

      <div className="flex justify-between">
        <span>On Track</span>
        <span className="font-medium">
          {deadlineStatus?.onTrack || 0}
        </span>
      </div>

      <div className="flex justify-between">
        <span>At Risk</span>
        <span className="font-medium">
          {deadlineStatus?.atRisk || 0}
        </span>
      </div>

      <div className="flex justify-between">
        <span>Overdue</span>
        <span className="font-medium">
          {deadlineStatus?.overdue || 0}
        </span>
      </div>

    </div>
  </div>

  {/* UPCOMING MILESTONES */}
  <div className="rounded-xl border bg-white p-5">
    <h2 className="mb-4 text-lg font-semibold">
      Upcoming Milestones
    </h2>

    {upcomingMilestones.length === 0 ? (
      <div className="text-sm text-gray-500">
        No upcoming milestones
      </div>
    ) : (
      <div className="space-y-3">
        {upcomingMilestones.map((m) => (
          <div
            key={m._id}
            className="flex items-center justify-between"
          >
            <span className="truncate">
              {m.name}
            </span>

            <span className="text-sm text-gray-500">
              {new Date(
                m.targetDate
              ).toLocaleDateString()}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>

  {/* QUICK ACTIONS */}
  <div className="rounded-xl border bg-white p-5">
    <h2 className="mb-4 text-lg font-semibold">
      Quick Actions
    </h2>

    <div className="space-y-3">

      <Link
        to="/projects"
        className="block rounded-lg border p-3 hover:bg-gray-50"
      >
        View Projects
      </Link>

      <Link
        to="/reports"
        className="block rounded-lg border p-3 hover:bg-gray-50"
      >
        Project Reports
      </Link>

      <Link
        to="/userReports"
        className="block rounded-lg border p-3 hover:bg-gray-50"
      >
        User Reports
      </Link>

      <Link
        to="/teamReports"
        className="block rounded-lg border p-3 hover:bg-gray-50"
      >
        Team Reports
      </Link>

    </div>
  </div>

</div>
  </div>
);
}